import { gql } from '@apollo/client';

export const LOGIN_STORE = gql`
  mutation LoginStore($email: String!, $password: String!) {
    loginStore(email: $email, password: $password) {
      error
      message
      token
      clientId
      storeId
      storeName
      portal
      storeType
    }
  }
`;

export const REQUEST_STORE_PASSWORD_RESET = gql`
  mutation RequestStorePasswordReset($email: String!, $resetBaseUrl: String) {
    requestStorePasswordReset(email: $email, resetBaseUrl: $resetBaseUrl) {
      error
      message
    }
  }
`;

export const RESET_STORE_PASSWORD = gql`
  mutation ResetStorePassword($token: String!, $password: String!) {
    resetStorePassword(token: $token, password: $password) {
      error
      message
    }
  }
`;

export const CHANGE_STORE_PASSWORD = gql`
  mutation ChangeStorePassword($currentPassword: String!, $newPassword: String!) {
    changeStorePassword(currentPassword: $currentPassword, newPassword: $newPassword) {
      error
      message
    }
  }
`;

export const VERIFY_STORE = gql`
  query verifyStore($token: String!) {
    verifyStore(token: $token) {
      error
      message
      clientId
      storeId
      storeName
      portal
      storeType
    }
  }
`;

export const GET_MY_STORE = gql`
  query GetMyStore {
    getMyStore {
      storeId
      clientId
      storeName
      emailId
      mobileNumber
      status
      portal
      storeType
      catalogType
      isSubscriptionActive
      isFreeAccount
    }
  }
`;

export const GET_CUSTOM_RETAILERS = gql`
  query GetCustomRetailers($search: String, $status: Boolean, $pageNo: Int, $limit: Int) {
    getCustomRetailers(search: $search, status: $status, pageNo: $pageNo, limit: $limit) {
      error
      message
      total
      items {
        id
        name
        contactPerson
        emailId
        mobileNumber
        address
        city
        pincode
        gstin
        tags
        status
        createdAt
      }
    }
  }
`;

export const CREATE_CUSTOM_RETAILER = gql`
  mutation CreateCustomRetailer($input: CustomRetailerInput!) {
    createCustomRetailer(input: $input) {
      error
      message
      retailer { id name }
    }
  }
`;

export const SET_CUSTOM_RETAILER_STATUS = gql`
  mutation SetCustomRetailerStatus($id: ID!, $status: Boolean!) {
    setCustomRetailerStatus(id: $id, status: $status) {
      error
      message
      retailer { id status }
    }
  }
`;

// ── Custom catalog (tags + collections) ──────────────────────────

export const GET_CUSTOM_TAGS = gql`
  query GetCustomTags {
    getCustomTags {
      error
      message
      items { id name color sortOrder status createdAt }
    }
  }
`;

export const CREATE_CUSTOM_TAG = gql`
  mutation CreateCustomTag($input: CustomTagInput!) {
    createCustomTag(input: $input) {
      error
      message
      tag { id name color sortOrder status }
    }
  }
`;

export const UPDATE_CUSTOM_TAG = gql`
  mutation UpdateCustomTag($id: ID!, $input: CustomTagUpdateInput!) {
    updateCustomTag(id: $id, input: $input) {
      error
      message
      tag { id name color sortOrder status }
    }
  }
`;

export const DELETE_CUSTOM_TAG = gql`
  mutation DeleteCustomTag($id: ID!) {
    deleteCustomTag(id: $id) {
      error
      message
    }
  }
`;

export const GET_CUSTOM_COLLECTIONS = gql`
  query GetCustomCollections($search: String, $status: Boolean, $pageNo: Int, $limit: Int) {
    getCustomCollections(search: $search, status: $status, pageNo: $pageNo, limit: $limit) {
      error
      message
      total
      items {
        id
        name
        description
        image
        tags
        status
        sortOrder
        createdAt
        updatedAt
      }
    }
  }
`;

export const CREATE_CUSTOM_COLLECTION = gql`
  mutation CreateCustomCollection($input: CustomCollectionInput!) {
    createCustomCollection(input: $input) {
      error
      message
      collection {
        id name description image tags status sortOrder
      }
    }
  }
`;

export const UPDATE_CUSTOM_COLLECTION = gql`
  mutation UpdateCustomCollection($id: ID!, $input: CustomCollectionUpdateInput!) {
    updateCustomCollection(id: $id, input: $input) {
      error
      message
      collection {
        id name description image tags status sortOrder
      }
    }
  }
`;

export const DELETE_CUSTOM_COLLECTION = gql`
  mutation DeleteCustomCollection($id: ID!) {
    deleteCustomCollection(id: $id) {
      error
      message
    }
  }
`;

// ── Custom products (ecommerce / clothing) ──────────────────────

const CUSTOM_PRODUCT_FIELDS = gql`
  fragment CustomProductFields on CustomProduct {
    id
    title
    description
    code
    barcode
    brand
    productType
    status
    mrp
    sellingPrice
    discount
    minPrice
    maxPrice
    currency
    trackInventory
    stock
    totalStock
    minimumOrderQty
    image
    gallery
    tags
    hasVariants
    optionTypes
    variants {
      id
      sku
      barcode
      options { name value }
      mrp
      sellingPrice
      discount
      stock
      image
      status
    }
    material
    careInstructions
    gender
    sizeChartUrl
    weight
    dimensions { length width height }
    sortOrder
    createdAt
    updatedAt
  }
`;

export const GET_CUSTOM_PRODUCTS = gql`
  ${CUSTOM_PRODUCT_FIELDS}
  query GetCustomProducts(
    $search: String
    $status: String
    $tag: String
    $pageNo: Int
    $limit: Int
  ) {
    getCustomProducts(
      search: $search
      status: $status
      tag: $tag
      pageNo: $pageNo
      limit: $limit
    ) {
      error
      message
      total
      items { ...CustomProductFields }
    }
  }
`;

export const GET_CUSTOM_PRODUCT = gql`
  ${CUSTOM_PRODUCT_FIELDS}
  query GetCustomProduct($id: ID!) {
    getCustomProduct(id: $id) {
      error
      message
      product { ...CustomProductFields }
    }
  }
`;

export const CREATE_CUSTOM_PRODUCT = gql`
  ${CUSTOM_PRODUCT_FIELDS}
  mutation CreateCustomProduct($input: CustomProductInput!) {
    createCustomProduct(input: $input) {
      error
      message
      product { ...CustomProductFields }
    }
  }
`;

export const UPDATE_CUSTOM_PRODUCT = gql`
  ${CUSTOM_PRODUCT_FIELDS}
  mutation UpdateCustomProduct($id: ID!, $input: CustomProductUpdateInput!) {
    updateCustomProduct(id: $id, input: $input) {
      error
      message
      product { ...CustomProductFields }
    }
  }
`;

export const SET_CUSTOM_PRODUCT_STATUS = gql`
  mutation SetCustomProductStatus($id: ID!, $status: String!) {
    setCustomProductStatus(id: $id, status: $status) {
      error
      message
      product { id status }
    }
  }
`;

export const DELETE_CUSTOM_PRODUCT = gql`
  mutation DeleteCustomProduct($id: ID!) {
    deleteCustomProduct(id: $id) {
      error
      message
    }
  }
`;

// ── Custom services (service-catalog stores) ────────────────────

const CUSTOM_SERVICE_FIELDS = gql`
  fragment CustomServiceFields on CustomService {
    id
    name
    description
    icon
    image
    status
    sortOrder
    tags
    subServices {
      id
      name
      description
      price
      duration
      featured
      sortOrder
      status
    }
    links {
      id
      name
      url
      username
      password
      notes
    }
    createdAt
    updatedAt
  }
`;

export const GET_CUSTOM_SERVICES = gql`
  ${CUSTOM_SERVICE_FIELDS}
  query GetCustomServices($search: String, $status: String, $tag: String, $pageNo: Int, $limit: Int) {
    getCustomServices(search: $search, status: $status, tag: $tag, pageNo: $pageNo, limit: $limit) {
      error
      message
      total
      items { ...CustomServiceFields }
    }
  }
`;

export const CREATE_CUSTOM_SERVICE = gql`
  ${CUSTOM_SERVICE_FIELDS}
  mutation CreateCustomService($input: CustomServiceInput!) {
    createCustomService(input: $input) {
      error
      message
      service { ...CustomServiceFields }
    }
  }
`;

export const UPDATE_CUSTOM_SERVICE = gql`
  ${CUSTOM_SERVICE_FIELDS}
  mutation UpdateCustomService($id: ID!, $input: CustomServiceUpdateInput!) {
    updateCustomService(id: $id, input: $input) {
      error
      message
      service { ...CustomServiceFields }
    }
  }
`;

export const SET_CUSTOM_SERVICE_STATUS = gql`
  mutation SetCustomServiceStatus($id: ID!, $status: String!) {
    setCustomServiceStatus(id: $id, status: $status) {
      error
      message
      service { id status }
    }
  }
`;

export const DELETE_CUSTOM_SERVICE = gql`
  mutation DeleteCustomService($id: ID!) {
    deleteCustomService(id: $id) {
      error
      message
    }
  }
`;

// ── Payment settings ────────────────────────────────────────────

export const UPDATE_PAYMENT_SETTINGS = gql`
  mutation UpdatePaymentSettings($input: PaymentSettingsInput!) {
    updatePaymentSettings(input: $input) {
      error
      message
      settings {
        paymentSettings {
          razorpay { enabled keyId hasSecret hasWebhookSecret }
        }
      }
    }
  }
`;

// ── Booking status (admin) ──────────────────────────────────────

export const UPDATE_BOOKING_STATUS = gql`
  mutation UpdateBookingStatus($bookingId: ID!, $status: String!, $note: String) {
    updateBookingStatus(bookingId: $bookingId, status: $status, note: $note) {
      error
      message
      booking { id status }
    }
  }
`;

// ── Subscription ─────────────────────────────────────────────

export const INITIATE_SUBSCRIPTION = gql`
  mutation InitiateSubscription {
    initiateSubscription {
      error
      message
      token
      paymentPageUrl
    }
  }
`;

export const CANCEL_SUBSCRIPTION = gql`
  mutation CancelSubscription($subscriptionId: String!) {
    cancelSubscription(subscriptionId: $subscriptionId) {
      error
      message
    }
  }
`;

export const GET_RECENT_SUBSCRIPTION_DETAIL = gql`
  query GetRecentSubscriptionDetail {
    getRecentSubscriptionDetail
  }
`;

export const GET_SUBSCRIPTION_HISTORY = gql`
  query GetSubscriptionHistory($pageNo: Int, $limit: Int) {
    getSubscriptionHistoryByPagination(pageNo: $pageNo, limit: $limit)
  }
`;

export const GET_PAYMENT_HISTORY_BY_SUBSCRIPTION = gql`
  query GetPaymentHistoryBySubscription($subscriptionId: String, $pageNo: Int, $limit: Int) {
    getPaymentHistoryBySubscription(
      subscriptionId: $subscriptionId
      pageNo: $pageNo
      limit: $limit
    )
  }
`;

// ── Field Users (FSR) ───────────────────────────────────────────
const FSR_FIELDS = gql`
  fragment FsrFields on Fsr {
    id
    name
    employeeId
    email
    mobile
    status
    reportingTo
    distributorId
    catalogAccess
    catalogCollections
    avatar
    color
    lastSeenAt
    assignedDistributors {
      distributorId
      catalogAccess
    }
    assignedRetailers {
      retailerId
    }
    createdAt
    updatedAt
  }
`;

export const GET_FSRS = gql`
  ${FSR_FIELDS}
  query GetFsrs(
    $page: Int
    $limit: Int
    $search: String
    $status: String
    $reportingTo: String
    $distributorId: String
  ) {
    getFsrs(
      page: $page
      limit: $limit
      search: $search
      status: $status
      reportingTo: $reportingTo
      distributorId: $distributorId
    ) {
      error
      message
      total
      page
      limit
      totalPages
      fsrs {
        ...FsrFields
      }
    }
  }
`;

export const GET_FSR_SUMMARY = gql`
  query GetFsrSummary {
    getFsrSummary {
      total
      active
      inactive
      suspended
      direct
      underDistributor
      fsrLimit
      isUnlimitedFsrs
      isFreeAccount
      isSubscriptionActive
      seatsRemaining
    }
  }
`;

export const CREATE_FSR = gql`
  ${FSR_FIELDS}
  mutation CreateFsr(
    $name: String!
    $email: String!
    $mobile: String
    $password: String
    $reportingTo: String
    $distributorId: String
    $catalogAccess: String
    $catalogCollections: [String!]
  ) {
    createFsr(
      name: $name
      email: $email
      mobile: $mobile
      password: $password
      reportingTo: $reportingTo
      distributorId: $distributorId
      catalogAccess: $catalogAccess
      catalogCollections: $catalogCollections
    ) {
      error
      message
      generatedPassword
      fsr {
        ...FsrFields
      }
    }
  }
`;

export const UPDATE_FSR = gql`
  ${FSR_FIELDS}
  mutation UpdateFsr(
    $id: ID!
    $name: String
    $email: String
    $mobile: String
    $password: String
    $status: String
    $reportingTo: String
    $distributorId: String
    $catalogAccess: String
    $catalogCollections: [String!]
  ) {
    updateFsr(
      id: $id
      name: $name
      email: $email
      mobile: $mobile
      password: $password
      status: $status
      reportingTo: $reportingTo
      distributorId: $distributorId
      catalogAccess: $catalogAccess
      catalogCollections: $catalogCollections
    ) {
      error
      message
      fsr {
        ...FsrFields
      }
    }
  }
`;

export const DELETE_FSR = gql`
  mutation DeleteFsr($id: ID!) {
    deleteFsr(id: $id) {
      error
      message
    }
  }
`;

export const SET_FSR_STATUS = gql`
  mutation SetFsrStatus($ids: [ID!]!, $status: String!) {
    setFsrStatus(ids: $ids, status: $status) {
      error
      message
      updated
    }
  }
`;

export const RESET_FSR_PASSWORD = gql`
  ${FSR_FIELDS}
  mutation ResetFsrPassword($id: ID!, $password: String) {
    resetFsrPassword(id: $id, password: $password) {
      error
      message
      generatedPassword
      fsr {
        ...FsrFields
      }
    }
  }
`;

export const SET_FSR_DISTRIBUTORS = gql`
  ${FSR_FIELDS}
  mutation SetFsrDistributors($fsrId: ID!, $distributors: [FsrDistributorAssignmentInput!]!) {
    setFsrDistributors(fsrId: $fsrId, distributors: $distributors) {
      error
      message
      fsr {
        ...FsrFields
      }
    }
  }
`;

export const SET_FSR_RETAILERS = gql`
  ${FSR_FIELDS}
  mutation SetFsrRetailers($fsrId: ID!, $retailerIds: [String!]!) {
    setFsrRetailers(fsrId: $fsrId, retailerIds: $retailerIds) {
      error
      message
      fsr {
        ...FsrFields
      }
    }
  }
`;

// ── Connector (B2B sync) — Distributors / Retailers ──────────────
const CONNECTOR_CUSTOMER_FIELDS = gql`
  fragment ConnectorCustomerFields on ConnectorCustomer {
    id
    firstName
    lastName
    displayName
    email
    mobile
    companyName
    city
    pincode
    userTags
    status
    userAccessLevel
    createdDate
  }
`;

export const GET_DISTRIBUTORS = gql`
  ${CONNECTOR_CUSTOMER_FIELDS}
  query GetDistributors($page: Int, $limit: Int, $search: String, $status: String) {
    getDistributors(page: $page, limit: $limit, search: $search, status: $status) {
      error
      message
      total
      page
      limit
      customers {
        ...ConnectorCustomerFields
      }
    }
  }
`;

export const GET_RETAILERS = gql`
  ${CONNECTOR_CUSTOMER_FIELDS}
  query GetRetailers($page: Int, $limit: Int, $search: String, $status: String) {
    getRetailers(page: $page, limit: $limit, search: $search, status: $status) {
      error
      message
      total
      page
      limit
      customers {
        ...ConnectorCustomerFields
      }
    }
  }
`;

/**
 * Create a retailer. The backend routes it for us — RiEtail stores push the
 * user upstream (user type resolved from the `storefront_only` access
 * level), custom stores write to their local Retailer collection.
 */
export const CREATE_RETAILER = gql`
  mutation CreateRetailer($input: RetailerCreateInput!) {
    createRetailer(input: $input) {
      error
      message
      retailerId
    }
  }
`;

// ── Catalog (B2B-synced products + collections) ─────────────────
const CONNECTOR_PRODUCT_FIELDS = gql`
  fragment ConnectorProductFields on ConnectorProduct {
    id
    name
    description
    type
    code
    status
    sellingPrice
    mrp
    discount
    image
    minimumOrderQty
    isMixedPack
    isConfigurable
    vendor
    tags
  }
`;

export const GET_CATALOG_PRODUCTS = gql`
  ${CONNECTOR_PRODUCT_FIELDS}
  query GetCatalogProducts($page: Int, $limit: Int, $search: String, $productType: String, $status: String) {
    getCatalogProducts(page: $page, limit: $limit, search: $search, productType: $productType, status: $status) {
      error
      message
      total
      page
      limit
      hasMore
      products {
        ...ConnectorProductFields
      }
    }
  }
`;

const CONNECTOR_COLLECTION_FIELDS = gql`
  fragment ConnectorCollectionFields on ConnectorCollection {
    id
    name
    description
    handle
    productCount
    image
    images
    source
    createdDate
  }
`;

export const GET_CATALOG_COLLECTIONS = gql`
  ${CONNECTOR_COLLECTION_FIELDS}
  query GetCatalogCollections($page: Int, $limit: Int, $search: String) {
    getCatalogCollections(page: $page, limit: $limit, search: $search) {
      error
      message
      total
      page
      limit
      hasMore
      collections {
        ...ConnectorCollectionFields
      }
    }
  }
`;

// ── Settings ────────────────────────────────────────────────
const SETTINGS_FIELDS = gql`
  fragment SettingsFields on Settings {
    storeId
    storeName
    contactPerson
    emailId
    mobileNumber
    gstin
    city
    address
    portal
    prefix
    notificationPrefs {
      bookingCreated
      bookingApproved
      bookingRejected
      b2bConfirmed
      b2bDispatched
      b2bDelivered
      visitReminder
      noCheckIn
      weeklyReport
      lowStock
    }
    purchaseFlow {
      defaultQtyMode
      approvalFlow
      bookingPermissions {
        allowDirectSubmit
        requirePhoto
        enablePackRatio
        moqRequired
        showMrp
      }
    }
    b2bConnection {
      connected
      connectorUrl
      storeType
      installedAt
    }
    paymentSettings {
      razorpay {
        enabled
        keyId
        hasSecret
        hasWebhookSecret
      }
    }
  }
`;

export const GET_SETTINGS = gql`
  ${SETTINGS_FIELDS}
  query GetSettings {
    getSettings {
      error
      message
      settings {
        ...SettingsFields
      }
    }
  }
`;

export const UPDATE_STORE_PROFILE = gql`
  ${SETTINGS_FIELDS}
  mutation UpdateStoreProfile($input: UpdateProfileInput!) {
    updateStoreProfile(input: $input) {
      error
      message
      settings {
        ...SettingsFields
      }
    }
  }
`;

export const UPDATE_NOTIFICATION_PREFS = gql`
  ${SETTINGS_FIELDS}
  mutation UpdateNotificationPrefs($input: NotificationPrefsInput!) {
    updateNotificationPrefs(input: $input) {
      error
      message
      settings {
        ...SettingsFields
      }
    }
  }
`;

export const UPDATE_PURCHASE_FLOW = gql`
  ${SETTINGS_FIELDS}
  mutation UpdatePurchaseFlow($input: PurchaseFlowInput!) {
    updatePurchaseFlow(input: $input) {
      error
      message
      settings {
        ...SettingsFields
      }
    }
  }
`;

// ── Visit Scheduler ─────────────────────────────────────────────
const VISIT_FIELDS = gql`
  fragment VisitFields on Visit {
    id
    fsrId
    date
    retailerId
    retailerName
    retailerCity
    retailerMobile
    priority
    scheduledTime
    sequence
    status
    markedDoneAt
    notes
    createdAt
  }
`;

export const GET_VISITS = gql`
  ${VISIT_FIELDS}
  query GetVisits($fsrId: ID!, $date: String!) {
    getVisits(fsrId: $fsrId, date: $date) {
      error
      message
      visits {
        ...VisitFields
      }
    }
  }
`;

export const GET_VISIT_DAY_STATS = gql`
  query GetVisitDayStats($date: String!) {
    getVisitDayStats(date: $date) {
      fsrsWithPlan
      storesScheduled
      completed
      pending
      skipped
    }
  }
`;

export const GET_VISIT_DAY_PROGRESS = gql`
  query GetVisitDayProgress($date: String!) {
    getVisitDayProgress(date: $date) {
      fsrId
      total
      done
      pending
    }
  }
`;

export const ADD_VISITS = gql`
  ${VISIT_FIELDS}
  mutation AddVisits($fsrId: ID!, $date: String!, $retailerIds: [String!]!, $scheduledTime: String) {
    addVisits(fsrId: $fsrId, date: $date, retailerIds: $retailerIds, scheduledTime: $scheduledTime) {
      error
      message
      visits {
        ...VisitFields
      }
    }
  }
`;

export const UPDATE_VISIT = gql`
  ${VISIT_FIELDS}
  mutation UpdateVisit($id: ID!, $input: VisitUpdateInput!) {
    updateVisit(id: $id, input: $input) {
      error
      message
      visit {
        ...VisitFields
      }
    }
  }
`;

export const MARK_VISIT_DONE = gql`
  ${VISIT_FIELDS}
  mutation MarkVisitDone($id: ID!) {
    markVisitDone(id: $id) {
      error
      message
      visit {
        ...VisitFields
      }
    }
  }
`;

export const REMOVE_VISIT = gql`
  mutation RemoveVisit($id: ID!) {
    removeVisit(id: $id) {
      error
      message
    }
  }
`;

export const REORDER_VISITS = gql`
  ${VISIT_FIELDS}
  mutation ReorderVisits($fsrId: ID!, $date: String!, $orderedIds: [ID!]!) {
    reorderVisits(fsrId: $fsrId, date: $date, orderedIds: $orderedIds) {
      error
      message
      visits {
        ...VisitFields
      }
    }
  }
`;

export const OPTIMIZE_VISIT_ROUTE = gql`
  ${VISIT_FIELDS}
  mutation OptimizeVisitRoute($fsrId: ID!, $date: String!) {
    optimizeVisitRoute(fsrId: $fsrId, date: $date) {
      error
      message
      visits {
        ...VisitFields
      }
    }
  }
`;

// ── Reports & Analytics ─────────────────────────────────────────
export const GET_REPORT_SUMMARY = gql`
  query GetReportSummary($fromDate: String, $toDate: String) {
    getReportSummary(fromDate: $fromDate, toDate: $toDate) {
      from
      to
      totalVisits
      completed
      pending
      skipped
      completionRate
      uniqueRetailers
      activeFsrs
      registeredFsrs
    }
  }
`;

export const GET_DAILY_VISIT_TREND = gql`
  query GetDailyVisitTrend($fromDate: String, $toDate: String) {
    getDailyVisitTrend(fromDate: $fromDate, toDate: $toDate) {
      date
      scheduled
      completed
    }
  }
`;

export const GET_FSR_LEADERBOARD = gql`
  query GetFsrLeaderboard($fromDate: String, $toDate: String, $limit: Int) {
    getFsrLeaderboard(fromDate: $fromDate, toDate: $toDate, limit: $limit) {
      fsrId
      name
      employeeId
      avatar
      color
      status
      totalVisits
      completed
      completionRate
    }
  }
`;

export const GET_STATUS_BREAKDOWN = gql`
  query GetStatusBreakdown($fromDate: String, $toDate: String) {
    getStatusBreakdown(fromDate: $fromDate, toDate: $toDate) {
      status
      count
    }
  }
`;

export const GET_TERRITORY_BREAKDOWN = gql`
  query GetTerritoryBreakdown($fromDate: String, $toDate: String, $limit: Int) {
    getTerritoryBreakdown(fromDate: $fromDate, toDate: $toDate, limit: $limit) {
      city
      totalVisits
      completed
    }
  }
`;

// ── Bookings ────────────────────────────────────────────────────
const BOOKING_FIELDS = gql`
  fragment BookingFields on Booking {
    id
    bookingNumber
    fsrId
    fsrName
    fsrEmployeeId
    retailerId
    retailerName
    retailerCity
    retailerMobile
    totalUnits
    subtotal
    tax
    shipping
    discount
    total
    status
    submittedAt
    approvedAt
    rejectedAt
    cancelledAt
    fulfilledAt
    approvedBy
    rejectionReason
    notes
    location { lat lng }
    b2bOrderId
    createdAt
    updatedAt
    lines {
      productId
      productName
      productCode
      productImage
      productType
      parentUnitPrice
      lineTotal
      entries {
        setId
        variantId
        quantity
        unitPrice
        taxRate
        taxId
        label
        sku
      }
    }
  }
`;

export const GET_BOOKINGS = gql`
  ${BOOKING_FIELDS}
  query GetBookings(
    $status: String
    $fsrId: String
    $retailerId: String
    $search: String
    $page: Int
    $limit: Int
  ) {
    getBookings(
      status: $status
      fsrId: $fsrId
      retailerId: $retailerId
      search: $search
      page: $page
      limit: $limit
    ) {
      total
      page
      limit
      bookings { ...BookingFields }
    }
  }
`;

export const GET_BOOKING = gql`
  ${BOOKING_FIELDS}
  query GetBooking($bookingId: ID!) {
    getBooking(bookingId: $bookingId) { ...BookingFields }
  }
`;

export const GET_BOOKING_STATS = gql`
  query GetBookingStats {
    getBookingStats {
      all
      pending
      approved
      rejected
      cancelled
      fulfilled
      totalValue
    }
  }
`;

export const APPROVE_BOOKING = gql`
  ${BOOKING_FIELDS}
  mutation ApproveBooking($bookingId: ID!) {
    approveBooking(bookingId: $bookingId) {
      error
      message
      booking { ...BookingFields }
    }
  }
`;

export const REJECT_BOOKING = gql`
  ${BOOKING_FIELDS}
  mutation RejectBooking($bookingId: ID!, $reason: String) {
    rejectBooking(bookingId: $bookingId, reason: $reason) {
      error
      message
      booking { ...BookingFields }
    }
  }
`;

// ── Admin Dashboard ─────────────────────────────────────────────
export const GET_DASHBOARD_KPIS = gql`
  query GetDashboardKpis {
    getDashboardKpis {
      revenueToday
      revenueDelta
      bookingsToday
      bookingsDelta
      avgOrderValueToday
      avgDelta
      activeFsrs
      totalFsrs
    }
  }
`;

export const GET_DASHBOARD_SALES_TREND = gql`
  query GetDashboardSalesTrend($range: String) {
    getDashboardSalesTrend(range: $range) {
      range
      points { date label revenue bookings }
    }
  }
`;

export const GET_DASHBOARD_STATUS_BREAKDOWN = gql`
  query GetDashboardStatusBreakdown {
    getDashboardStatusBreakdown {
      total
      items { status count }
    }
  }
`;

export const GET_DASHBOARD_ACTIVE_FSRS = gql`
  query GetDashboardActiveFsrs($limit: Int) {
    getDashboardActiveFsrs(limit: $limit) {
      fsrId
      name
      employeeId
      color
      avatar
      reportingTo
      distributorId
      todayPlan
      todayDone
    }
  }
`;

export const GET_DASHBOARD_RECENT_BOOKINGS = gql`
  query GetDashboardRecentBookings($limit: Int) {
    getDashboardRecentBookings(limit: $limit) {
      id
      bookingNumber
      fsrId
      fsrName
      retailerName
      retailerCity
      total
      status
      submittedAt
    }
  }
`;
